<template>
  <section class="cr" data-testid="campaign-results">
    <div class="cr-head">
      <div>
        <span class="cr-label">Goal</span>
        <span class="cr-metric">{{ objectiveLabel }}</span>
      </div>
      <div class="cr-value">
        <span class="cr-now">{{ fmt(measured) }}</span>
        <span v-if="campaign?.target_value" class="cr-target">/ {{ fmt(campaign.target_value) }}</span>
        <span class="cr-unit">{{ objectiveLabel.toLowerCase() }}</span>
      </div>
    </div>
    <div v-if="campaign?.target_value" class="cr-progress"><div class="cr-fill" :style="{ width: progressPct + '%' }"></div></div>

    <div class="cr-metrics">
      <div class="cr-stat"><span class="cr-stat-val">{{ fmt(totals.impressions) }}</span><span class="cr-stat-label">Views</span></div>
      <div class="cr-stat"><span class="cr-stat-val">{{ fmt(totals.engagement) }}</span><span class="cr-stat-label">Engagement</span></div>
      <div class="cr-stat"><span class="cr-stat-val">{{ fmt(totals.posts) }}</span><span class="cr-stat-label">Posts</span></div>
    </div>

    <div v-if="report && report.byChannel.length" class="cr-channels">
      <span class="cr-sub">By channel (avg {{ report.objective }})</span>
      <div v-for="c in report.byChannel" :key="c.key" class="cr-rank">
        <span class="cr-chan" :data-platform="c.key">{{ channelLabel(c.key) }}</span>
        <div class="cr-bar"><div class="cr-bar-fill" :style="{ width: barPct(c.avg) + '%' }"></div></div>
        <b class="cr-rank-val">{{ c.avg.toFixed(1) }}</b>
      </div>
    </div>

    <div v-if="recSummary" class="cr-rec" data-testid="cr-rec">
      <span class="cr-sub">Recommendation</span>
      <p class="cr-rec-text">{{ recSummary }}</p>
      <p class="cr-rec-hint">Apply it from the chat to draft the next round.</p>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  campaign: { type: Object, default: null },
  reports: { type: Object, default: () => ({}) },
})

const objective = computed(() => props.campaign?.objective || 'engagement')
const objectiveLabel = computed(() => ({ impressions: 'Impressions', engagement: 'Engagement', followers: 'Followers' }[objective.value] || objective.value))
const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString())
const channelLabel = (c) => ({ 'linkedin-page': 'LinkedIn', linkedin: 'LinkedIn', x: 'X', twitter: 'X', instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', tiktok: 'TikTok', threads: 'Threads' }[c] || c)

const waves = computed(() => props.campaign?.waves || [])

// All measured post metrics across every cycle.
const measuredPosts = computed(() => {
  const out = []
  for (const w of waves.value) {
    const r = props.reports[w.id]
    for (const rp of r?.posts || []) if (rp.measured) out.push(rp)
  }
  return out
})
const totals = computed(() => {
  const mp = measuredPosts.value
  const sum = (k) => mp.reduce((n, p) => n + (p[k] || 0), 0)
  return {
    impressions: sum('impressions'),
    engagement: sum('comments') + sum('shares') + sum('clicks'),
    posts: waves.value.reduce((n, w) => n + (w.posts || []).filter((p) => p.scheduled_at).length, 0),
  }
})
const measured = computed(() => {
  if (objective.value === 'impressions') return totals.value.impressions
  if (objective.value === 'followers') return measuredPosts.value.reduce((n, p) => n + (p.followers || 0), 0)
  return totals.value.engagement
})
const progressPct = computed(() => {
  const t = props.campaign?.target_value
  return t ? Math.max(0, Math.min(100, Math.round((measured.value / t) * 100))) : 0
})

// Most recent live cycle's report drives the by-channel + recommendation.
const report = computed(() => {
  for (let i = waves.value.length - 1; i >= 0; i -= 1) {
    if (['scheduled', 'observing', 'evaluated'].includes(waves.value[i].status)) return props.reports[waves.value[i].id] || null
  }
  return null
})
const barMax = computed(() => Math.max(1, ...((report.value?.byChannel) || []).map((c) => c.avg)))
const barPct = (v) => Math.round((v / barMax.value) * 100)
const recSummary = computed(() => {
  const s = report.value?.suggestion
  if (!s) return ''
  const parts = []
  if (s.keep?.length) parts.push(`Scale ${s.keep.join(', ')}`)
  if (s.drop?.length) parts.push(`Drop ${s.drop.join(', ')}`)
  if (s.rewrite?.length) parts.push(`Rewrite ${s.rewrite.join(', ')}`)
  return parts.join(' · ')
})
</script>

<style scoped>
.cr { background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md); padding: 16px 18px; box-shadow: var(--elev-sm); color: var(--text-primary); }
.cr-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.cr-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin-right: 8px; }
.cr-metric { font-size: 1.05rem; font-weight: 700; }
.cr-value { display: flex; align-items: baseline; gap: 4px; }
.cr-now { font-size: 1.5rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.cr-target { color: var(--text-secondary); font-weight: 600; }
.cr-unit { font-size: 0.72rem; color: var(--text-muted); }
.cr-progress { height: 8px; border-radius: 4px; background: rgba(255,255,255,0.08); overflow: hidden; margin: 12px 0 4px; }
.cr-fill { height: 100%; background: var(--accent); border-radius: 4px; transition: width 0.3s; }

.cr-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 14px; }
.cr-stat { display: flex; flex-direction: column; gap: 2px; }
.cr-stat-val { font-size: 1.2rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.cr-stat-label { font-size: 0.7rem; color: var(--text-secondary); }

.cr-sub { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted); }
.cr-channels { margin-top: 16px; padding-top: 14px; border-top: var(--glass-border); display: flex; flex-direction: column; gap: 8px; }
.cr-rank { display: grid; grid-template-columns: 84px 1fr 40px; align-items: center; gap: 10px; font-size: 0.8rem; }
.cr-chan { color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cr-bar { height: 6px; border-radius: 3px; background: rgba(255,255,255,0.08); overflow: hidden; }
.cr-bar-fill { height: 100%; background: var(--accent); border-radius: 3px; }
.cr-rank-val { text-align: right; font-variant-numeric: tabular-nums; }

.cr-rec { margin-top: 16px; padding-top: 14px; border-top: var(--glass-border); }
.cr-rec-text { font-size: 0.9rem; color: var(--accent-light); font-weight: 600; margin: 6px 0 0; }
.cr-rec-hint { font-size: 0.74rem; color: var(--text-muted); margin: 4px 0 0; }
</style>
